<template>
  <div class="add-ons">
    <div class="add-ons__content">
      <div class="add-ons__title">Add-ons</div>
      <div class="add-ons__items">
        <div class="add-ons__certifications" v-if="certificationsList.length">
          <div
            :class="[
              'add-ons__certifications-wrapper',
              certificationsToggler && 'add-ons__certifications-wrapper--show',
            ]"
          >
            <div
              @click="certificationsToggler = !certificationsToggler"
              :class="[
                'add-ons__certifications-placeholder',
                certificationsToggler && 'add-ons__certifications-placeholder--show',
              ]"
            >
              <div class="add-ons__certifications-placeholder-text">Certifications</div>
              <Pill infoWhite v-if="selectedItems > 0"> {{ selectedItems }} selected </Pill>
              <ChevronDownIcon class="add-ons__chevron" />
            </div>
            <div
              v-show="certificationsToggler"
              :class="[
                'add-ons__certifications-items-wrapper',
                certificationsToggler && 'add-ons__certifications-items-wrapper--show',
              ]"
            >
              <div
                class="add-ons__certifications-item"
                v-for="certification in certificationsList"
                :key="certification.merchant_id"
                @click="handleItemRowClock(certification)"
              >
                <div class="add-ons__certifications-item-name">
                  <div class="add-ons__certifications-item-name-text">{{ certification.name }}</div>
                  <div
                    class="add-ons__certifications-item-name-included"
                    v-if="certification.included"
                  >
                    {{
                      isFreeTrial
                        ? 'Free with this subscription after trial period ends'
                        : 'Free with this subscription'
                    }}
                  </div>
                </div>
                <div class="add-ons__certifications-item-price">
                  {{ certification.included ? 'Free' : `$${certification.price_cents / 100}` }}
                </div>
                <div class="add-ons__certifications-item-check">
                  <CheckGreen
                    class="add-ons__certifications-item-check-included"
                    v-if="certification.included"
                  />
                  <CheckBox
                    v-else
                    :cartTheme="true"
                    :isSelected="certification.addOnChecked"
                    @click="updateSelectedCertification(certification)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="add-ons__skill-dive" v-if="!userHasSkillDive && showSkillDiveAddOns">
          <div
            :class="[
              'add-ons__skill-dive-wrapper',
              skillDiveToggler && 'add-ons__skill-dive-wrapper--show',
            ]"
          >
            <div
              @click="skillDiveToggler = !skillDiveToggler"
              :class="[
                'add-ons__skill-dive-placeholder',
                skillDiveToggler && 'add-ons__skill-dive-placeholder--show',
              ]"
            >
              <div class="add-ons__skill-dive-placeholder-text">
                <img
                  :src="skillDiveLogoBadge"
                  alt="Skill Dive Experience"
                  class="add-ons__skill-dive-placeholder-logo"
                />
              </div>
              <Pill infoWhite v-if="selectedItemsSD > 0"> {{ selectedItemsSD }} selected </Pill>
              <ChevronDownIcon class="add-ons__chevron" />
            </div>
            <div
              :class="[
                'add-ons__skill-dive-items-wrapper',
                skillDiveToggler && 'add-ons__skill-dive-items-wrapper--show',
              ]"
            >
              <PlanSkillDiveItemCard asAddOns />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import CheckGreenImg from '@/assets/icons/check_green.svg'
import CheckBox from '@/components/shared/CheckBox.vue'
import Pill from '@/components/shared/Pill.vue'
import ChevronDownIcon from '@/assets/icons/chevron_down.svg'
import CONSTANTS from '@/constants'
import skillDiveLogo from '@/assets/images/hero-section/skill-dive/skill-dive-logo.png'
import PlanSkillDiveItemCard from '@/components/plan-item-card/PlanSkillDiveItemCard.vue'

export default {
  name: 'AddOns',
  components: {
    CheckBox,
    Pill,
    CheckGreen: { ...CheckGreenImg, name: 'CheckGreenImg' },
    ChevronDownIcon: { ...ChevronDownIcon, name: 'ChevronDownIcon' },
    PlanSkillDiveItemCard,
  },
  data() {
    return {
      certificationsList: [],
      certificationsToggler: false,
      skillDiveToggler: false,
      selectedItems: 0,
      selectedItemsSD: 0,
      skillDiveLogoBadge: skillDiveLogo,
    }
  },
  props: {
    certifications: {
      type: Array,
      required: true,
    },
    selectedPlanCode: {
      type: String,
      required: false,
    },
    isFreeTrial: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    ...mapState({
      shoppingCart: state => state.shoppingCart.shoppingCart,
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
      userHasSkillDive: state => state.subscriptions.userHasSkillDive,
    }),
    ...mapGetters({
      isOnTrialSubscription: 'subscriptions/isOnTrialSubscription',
    }),
    isCartFlowPage() {
      return ['payment', 'confirmation'].includes(this.$route.name)
    },
    cartHasSubscription() {
      const itemsId = this.shoppingCart.map(item => item.merchant_id)
      return itemsId.some(code => Object.values(CONSTANTS.PLANS).includes(code))
    },
    cartHasFundamentalsSubscription() {
      const { PLANS } = CONSTANTS
      const itemsId = this.shoppingCart.map(item => item.merchant_id)
      return itemsId.some(code => [PLANS.MONTHLY, PLANS.ANNUAL].includes(code))
    },
    userHasFundamentalsSubscription() {
      const { PLANS } = CONSTANTS
      const userHasFundamentals = !!this.usersCurrentSubscriptions.find(subscription =>
        [PLANS.MONTHLY, PLANS.ANNUAL].includes(subscription.plan_code)
      )
      return this.usersCurrentSubscriptions.length > 0 && userHasFundamentals
    },
    showSkillDiveAddOns() {
      const plan = this.shoppingCart.find(
        item => item.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL
      )
      if (!plan) return true
      return plan && plan.selectedAsAddOns
    },
  },
  methods: {
    ...mapActions({
      addToShoppingCart: 'shoppingCart/addProductToShoppingCart',
      removeFromShoppingCart: 'shoppingCart/removeProductFromShoppingCart',
    }),
    updateSelectedCertification(certification) {
      const exists = this.shoppingCart.find(item => item.id === certification.id)
      if (exists) {
        certification.addOn = false
        certification.addOnChecked = false
        this.removeFromShoppingCart(certification)
        this.selectedItems = this.selectedItems - 1
      } else {
        certification.addOn = true
        certification.addOnChecked = true
        this.addToShoppingCart(certification)
        this.selectedItems = this.selectedItems + 1
      }
    },
    handleItemRowClock(certification) {
      if (!certification.included) this.updateSelectedCertification(certification)
    },
    setCertificationsList() {
      const { PLANS, VOUCHERS } = CONSTANTS
      const PREMIUM_PLANS = [
        PLANS.PREMIUM,
        PLANS.INE_PREMIUM_BOOGO_2Y,
        PLANS.BLKFRIDAY,
        PLANS.PTA,
        PLANS.PREMIUM2YR,
        PLANS.BAH,
        PLANS.PREMIUM_MONTHLY,
        PLANS.EDA_PREMIUM,
        PLANS.EDA_PREMIUM_CONT,
      ]
      if (this.isCartFlowPage) {
        // if user isOnTrialSubscription period, user should not be able to buy VOUCHERS.EJPT
        if (this.isOnTrialSubscription) {
          this.certificationsList = this.certifications.filter(
            item => item.merchant_id !== VOUCHERS.EJPT
          )
        } else {
          // remove VOUCHERS.EDA if userHasFundamentalsSubscription
          if (this.userHasFundamentalsSubscription || this.cartHasFundamentalsSubscription) {
            this.certificationsList = this.certifications.filter(item => {
              return item.merchant_id !== VOUCHERS.EDA
            })
          } else {
            this.certificationsList = this.certifications
          }
          this.selectedItems = this.certificationsList.filter(cert => cert.addOnChecked).length
        }
      } else {
        const itemsId = this.shoppingCart.map(item => item.merchant_id)
        // remove VOUCHERS.EDA if cart Has NO PREMIUM_PLANS Subscription
        if (!itemsId.some(code => PREMIUM_PLANS.includes(code))) {
          itemsId.push(VOUCHERS.EDA)
        }
        // remove VOUCHERS.EJPT if cart Has NO Subscription
        if (!this.cartHasSubscription) {
          const vouchersEjptV2 = itemsId.includes(VOUCHERS.EJPT)
          if (vouchersEjptV2) {
            const vouchersEjptV2Obj = this.shoppingCart.find(item => {
              return item.merchant_id === VOUCHERS.EJPT
            })
            this.removeFromShoppingCart(vouchersEjptV2Obj)
            vouchersEjptV2Obj.addOn = false
            vouchersEjptV2Obj.addOnChecked = false
          }
          // adding voucher merchant_id so is not added on the list of addOns by the filter
          itemsId.push(VOUCHERS.EJPT)
          const cartHasMonthlyEjpt = itemsId.includes([PLANS.MONTHLY_EJPT, PLANS.QUARTERLY_EJPT])
          if (cartHasMonthlyEjpt) ItemsId.splice(itemsId.indexOf(VOUCHERS.EJPT), 1)
        }
        // if voucher was added to cart as AddOn, it should remain as an AddOn
        const vouchersOnCart = itemsId.some(code => Object.values({ ...VOUCHERS }).includes(code))
        if (vouchersOnCart) {
          this.certifications.forEach(item => {
            if (item.addOn) {
              itemsId.splice(itemsId.indexOf(item.merchant_id), 1)
            }
          })
        }
        this.certificationsList = this.certifications.filter(item => {
          if (item.addOnChecked) {
            this.selectedItems = this.selectedItems + 1
          }
          return !itemsId.includes(item.merchant_id)
        })
      }
      this.certificationsList = this.certificationsList.reverse()
    },
  },
  mounted() {
    this.setCertificationsList()
  },
  watch: {
    shoppingCart: {
      immediate: true,
      handler(items) {
        const { PLANS } = CONSTANTS
        this.certificationsList.forEach(item => {
          if (this.shoppingCart.length === 0) item.addOnChecked = false
          if (this.selectedPlanCode === '' || this.selectedPlanCode === PLANS.SKILL_DIVE_ANNUAL) {
            item.included = false
          }
        })
        const cartHasSkillDive = items.find(i => i.merchant_id === PLANS.SKILL_DIVE_ANNUAL)
        if (cartHasSkillDive) {
          this.selectedItemsSD = cartHasSkillDive.addOnsOnCart.length
        } else {
          this.selectedItemsSD = 0
        }
        if (!this.cartHasSubscription) this.setCertificationsList()
        this.$forceUpdate()
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.add-ons {
  &__content {
    margin: 20px 0;
    text-align: left;
  }
  &__title {
    font-weight: 700;
    font-size: rem(20px);
    line-height: 26px;
    margin-bottom: 10px;
  }
  &__items {
    display: flex;
    flex-flow: column;
    gap: 10px;
  }
  &__certifications,
  &__skill-dive {
    cursor: pointer;
    &-wrapper {
      height: 55px;
      background: var(--gray-3);
      border-radius: 20px;
      transition: 0.3s all ease-in-out;
      &--show {
        height: 100%;
      }
    }
    &-placeholder {
      height: 55px;
      border-radius: 20px;
      background: rgb(30, 30, 30);
      background: radial-gradient(
          circle at 120% 120%,
          rgb(227, 163, 101, 0.9) 15%,
          rgb(132, 221, 192.5) 30%,
          rgba(51, 51, 51, 0) 50%
        ),
        linear-gradient(90deg, rgb(51, 51, 51) 65%, rgb(0, 204, 217) 85%, rgb(0, 205, 57) 100%);
      background-size: 50%, cover;
      background-position: 100%, center;
      background-repeat: no-repeat;
      display: flex;
      padding: 5px 30px;
      align-items: center;
      font-size: rem(20px);
      font-weight: 700;
      line-height: rem(26px);
      justify-content: space-between;
      gap: 10px;
      .pill {
        white-space: nowrap;
        margin-left: auto;
      }
      &--show {
        border-radius: 20px 20px 0 0;
      }
    }
    &-items {
      display: flex;
      flex-flow: column;
      row-gap: 10px;
      &-wrapper {
        width: 100%;
        height: 0px;
        transition: 0.2s all ease-in-out;
        opacity: 0;
        padding: 20px;
        display: flex;
        flex-flow: column;
        gap: 5px;
        &--show {
          opacity: 1;
          height: 100%;
          cursor: auto;
        }
      }
    }
    &-item {
      display: grid;
      grid-template-columns: auto 70px 50px;
      padding: 10px;
      border-radius: 10px;
      align-items: center;
      background: var(--glass-mid-gray);
      height: 46px;
      align-content: center;
      &-check {
        display: flex;
        justify-content: center;
        .check-box {
          display: flex;
          align-items: center;
        }
        &-included {
          margin: 0 auto;
        }
      }
      &-name {
        font-weight: 500;
        font-size: rem(14px);
        line-height: rem(20px);
        &-included {
          font-weight: 450;
          font-size: rem(12px);
          line-height: rem(14px);
          color: var(--strong-light-green-b5);
          margin-top: 5px;
        }
      }
      &-price {
        text-align: right;
        font-size: rem(20px);
        font-weight: 700;
        line-height: rem(26px);
      }
    }
  }
  &__certifications {
    &-item {
      cursor: pointer;
      &:hover {
        background: var(--glass-extra-light);
      }
    }
  }
  &__skill-dive {
    &-placeholder {
      background: rgb(30, 30, 30);
      background: linear-gradient(
        90deg,
        rgba(51, 51, 51, 1) 65%,
        rgba(111, 11, 248, 1) 85%,
        rgba(0, 204, 217, 1) 100%
      );
      &-logo {
        width: 130px;
        height: 25px;
      }
    }
  }
}
</style>
